import { getPanchayatDataProvider } from "./panchayat-data"
import { Language } from "./i18n"

export interface LocationNames {
  districtName: string
  blockName: string
  villageName: string
}

// Helper to pick the correct language field
function pickName(item: { en: string; hi: string; pa: string }, lang: Language): string {
  if (lang === 'hi') return item.hi || item.en
  if (lang === 'pa') return item.pa || item.en
  return item.en
}

export async function resolveLocationNames(
  districtVal: string,
  blockVal: string,
  villageVal: string,
  lang: Language
): Promise<LocationNames> {
  // Default fallback is the value itself (English/ID)
  let dName = districtVal || ""
  let bName = blockVal || ""
  let vName = villageVal || ""

  const normalize = (s: string) =>
    String(s || "")
      .normalize("NFC")
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, "")
      .trim()

  const dVal = (districtVal || "").trim()
  const bVal = (blockVal || "").trim()
  const vVal = (villageVal || "").trim()

  const dNorm = normalize(dVal)
  const bNorm = normalize(bVal)
  const vNorm = normalize(vVal)

  try {
    const provider = getPanchayatDataProvider()
    const matches = (entity: any, targetNorm: string) => {
      if (!entity || !targetNorm) return false
      const id = normalize(String(entity.id || ""))
      const code = normalize(String(entity.code || ""))
      const names = entity.names || {}
      return (
        (id && id === targetNorm) ||
        (code && code === targetNorm) ||
        normalize(String(names.en || "")) === targetNorm ||
        normalize(String(names.hi || "")) === targetNorm ||
        normalize(String(names.pa || "")) === targetNorm
      )
    }

    const districts = await provider.getDistricts()
    const district = dVal ? districts.find((entry) => matches(entry, dNorm)) : null
    if (district?.names) dName = pickName(district.names, lang)

    if (!district || !bVal) {
      return {
        districtName: dName,
        blockName: bName,
        villageName: vName,
      }
    }

    const blocks = await provider.getBlocks(district.id)
    const block = blocks.find((entry) => matches(entry, bNorm)) || null
    if (block?.names) bName = pickName(block.names, lang)

    if (!block || !vVal) {
      return {
        districtName: dName,
        blockName: bName,
        villageName: vName,
      }
    }

    const gps = await provider.getGramPanchayats(block.id, district.id)
    const village = gps.find((entry) => matches(entry, vNorm)) || null
    if (village?.names) vName = pickName(village.names, lang)
  } catch (err) {
    console.error("[NameResolver] Failed to resolve names", err)
  }

  return {
    districtName: dName,
    blockName: bName,
    villageName: vName
  }
}
